import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Stethoscope, Search, Trash2, Eye, FileText, Calendar, User, FileSignature, ListPlus, Plus,
} from "lucide-react";
import { toast } from "sonner";

const ar_service_label = (val) => {
  const map = {
    implant: "زراعة", crown: "تركيب", root_canal: "علاج عصب",
    extraction: "خلع", sinus_lift: "رفع جيب", pontic: "سن معلق",
    IMPLANT: "زراعة", ABUTMENT: "تركيبة زرعة", BONE_GR: "زراعة عظم",
    GUM_GR: "زراعة لثة", CROWN: "تلبيسة", RCT: "علاج عصب",
    EXO: "خلع", SINUS: "رفع جيب", PONTIC: "سن معلق",
    PERIO: "لثة / جراحة", RESTO: "حشوات", INLAY: "حشوات عصبية",
    VENEER: "فينيير", PC: "دعامة وتد", RCTRIX: "إعادة عصب", RCT2: "عصب",
    ORTHO: "تقويم", TEMP_CROWN: "تركيبة مؤقتة",
  };
  return map[val] || val;
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} - ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// ============== Plan Card (defined outside parent to avoid re-mounting) ==============
const PlanCard = ({ plan, onView, onDelete }) => (
  <Card className="card-hover border-0 shadow-sm" data-testid={`plan-card-${plan.id}`}>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between mb-3">
        <div className="w-12 h-12 rounded-xl bg-[#F5F3F9] flex items-center justify-center">
          <Stethoscope className="w-6 h-6 text-[#655dc6]" />
        </div>
        {plan.total_price && (
          <Badge className="bg-[#655dc6] text-white hover:bg-[#5449a8]">
            {plan.total_price} ريال
          </Badge>
        )}
      </div>
      <h3 className="font-bold text-lg text-[#655dc6] mb-1 flex items-center gap-2">
        <User className="w-4 h-4" />
        {plan.patient_name}
      </h3>
      <div className="text-sm text-slate-500 space-y-1 mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" />
          <span>رقم الملف: {plan.file_number || "—"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5" />
          <span>{formatDate(plan.created_at)}</span>
        </div>
        <div className="text-xs">عدد الخدمات: {plan.services?.length || 0}</div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" size="sm" onClick={onView}
          data-testid={`view-plan-${plan.id}`}>
          <Eye className="w-4 h-4 ml-1" />عرض
        </Button>
        <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50"
          onClick={onDelete} data-testid={`delete-plan-${plan.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

// ============== Consent Card ==============
const ConsentCard = ({ rec, onView, onDelete }) => (
  <Card className="card-hover border-0 shadow-sm" data-testid={`consent-card-${rec.id}`}>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between mb-3">
        <div className="w-12 h-12 rounded-xl bg-[#F5F3F9] flex items-center justify-center">
          <FileSignature className="w-6 h-6 text-[#655dc6]" />
        </div>
      </div>
      <h3 className="font-bold text-sm text-[#655dc6] mb-1 line-clamp-2">{rec.consent_title}</h3>
      <div className="text-sm text-slate-500 space-y-1 mb-3">
        <div className="flex items-center gap-2"><User className="w-3.5 h-3.5" /><span>{rec.patient_name}</span></div>
        <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" /><span>رقم الملف: {rec.file_number || "—"}</span></div>
        <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /><span>{formatDate(rec.created_at)}</span></div>
        {rec.teeth?.length > 0 && (<div className="text-xs">أسنان: {rec.teeth.join(", ")}</div>)}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" size="sm" onClick={onView}
          data-testid={`view-consent-${rec.id}`}>
          <Eye className="w-4 h-4 ml-1" />عرض
        </Button>
        <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50"
          onClick={onDelete} data-testid={`delete-consent-${rec.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

const TreatmentPlans = () => {
  const { token } = useAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Plans state
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [planSearch, setPlanSearch] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Consents state
  const [consents, setConsents] = useState([]);
  const [consentsLoading, setConsentsLoading] = useState(true);
  const [consentSearch, setConsentSearch] = useState("");
  const [selectedConsent, setSelectedConsent] = useState(null);

  // Services catalog state
  const [svcCatalog, setSvcCatalog] = useState([]);
  const [newSvcAr, setNewSvcAr] = useState("");
  const [newSvcEn, setNewSvcEn] = useState("");

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API}/treatment-services`);
      setSvcCatalog(res.data || []);
    } catch { /* fallback to static labels */ }
  };

  const addService = async () => {
    if (!newSvcAr.trim()) { toast.error("اسم الخدمة بالعربية مطلوب"); return; }
    try {
      await axios.post(`${API}/treatment-services`, { ar: newSvcAr, en: newSvcEn }, authHeaders);
      toast.success("تمت إضافة الخدمة — ستظهر فوراً في الخطة العلاجية");
      setNewSvcAr(""); setNewSvcEn("");
      fetchServices();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل في الإضافة"); }
  };

  const deleteService = async (s) => {
    if (!window.confirm(`حذف خدمة "${s.ar}" من قائمة الخطة العلاجية؟`)) return;
    try {
      await axios.delete(`${API}/treatment-services/${s.id}`, authHeaders);
      toast.success("تم حذف الخدمة");
      fetchServices();
    } catch { toast.error("فشل في الحذف"); }
  };

  const svcLabel = (val) => {
    const hit = svcCatalog.find(s => s.value === val);
    return hit ? hit.ar : ar_service_label(val);
  };

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await axios.get(`${API}/treatment-plans`, authHeaders);
      setPlans(res.data);
    } catch {
      toast.error("فشل في تحميل الخطط العلاجية");
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchConsents = async () => {
    setConsentsLoading(true);
    try {
      const res = await axios.get(`${API}/consent-records`, authHeaders);
      setConsents(res.data);
    } catch {
      toast.error("فشل في تحميل الإقرارات");
    } finally {
      setConsentsLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); fetchConsents(); fetchServices(); }, []); // eslint-disable-line

  const handleDeletePlan = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه الخطة العلاجية؟")) return;
    try {
      await axios.delete(`${API}/treatment-plans/${id}`, authHeaders);
      toast.success("تم الحذف بنجاح");
      fetchPlans();
    } catch {
      toast.error("فشل في الحذف");
    }
  };

  const handleDeleteConsent = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الإقرار؟")) return;
    try {
      await axios.delete(`${API}/consent-records/${id}`, authHeaders);
      toast.success("تم الحذف بنجاح");
      fetchConsents();
    } catch {
      toast.error("فشل في الحذف");
    }
  };

  const filteredPlans = plans.filter(p =>
    !planSearch ||
    p.patient_name?.toLowerCase().includes(planSearch.toLowerCase()) ||
    p.file_number?.toLowerCase().includes(planSearch.toLowerCase())
  );

  const filteredConsents = consents.filter(c =>
    !consentSearch ||
    c.patient_name?.toLowerCase().includes(consentSearch.toLowerCase()) ||
    c.file_number?.toLowerCase().includes(consentSearch.toLowerCase()) ||
    c.consent_title?.includes(consentSearch)
  );

  // ============== Render ==============

  return (
    <div className="space-y-6" dir="rtl" data-testid="treatment-plans-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">السجلات الطبية</h1>
        <p className="text-slate-500 mt-1">
          سجل الخطط العلاجية والإقرارات المُصدَّرة
        </p>
      </div>

      <Tabs defaultValue="plans" dir="rtl" className="space-y-4">
        <TabsList className="grid grid-cols-3 w-full max-w-xl">
          <TabsTrigger value="plans" className="gap-2" data-testid="tab-plans">
            <Stethoscope className="w-4 h-4" />
            الخطط العلاجية ({plans.length})
          </TabsTrigger>
          <TabsTrigger value="consents" className="gap-2" data-testid="tab-consents">
            <FileSignature className="w-4 h-4" />
            الإقرارات ({consents.length})
          </TabsTrigger>
          <TabsTrigger value="services" className="gap-2" data-testid="tab-services">
            <ListPlus className="w-4 h-4" />
            الخدمات ({svcCatalog.length})
          </TabsTrigger>
        </TabsList>

        {/* TREATMENT PLANS TAB */}
        <TabsContent value="plans" className="space-y-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="ابحث باسم المراجع أو رقم الملف..."
                  value={planSearch}
                  onChange={(e) => setPlanSearch(e.target.value)}
                  className="pr-10"
                  data-testid="treatment-plans-search"
                />
              </div>
            </CardContent>
          </Card>

          {plansLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
            </div>
          ) : filteredPlans.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center">
                <Stethoscope className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-[#655dc6] mb-2">لا توجد خطط علاجية</h3>
                <p className="text-slate-500">{planSearch ? "لم يتم العثور على نتائج مطابقة" : "لم يتم إنشاء أي خطة علاجية بعد"}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPlans.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onView={() => setSelectedPlan(plan)}
                  onDelete={() => handleDeletePlan(plan.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* CONSENTS TAB */}
        <TabsContent value="consents" className="space-y-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="ابحث باسم المراجع أو رقم الملف أو نوع الإقرار..."
                  value={consentSearch}
                  onChange={(e) => setConsentSearch(e.target.value)}
                  className="pr-10"
                  data-testid="consents-search"
                />
              </div>
            </CardContent>
          </Card>

          {consentsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
            </div>
          ) : filteredConsents.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center">
                <FileSignature className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-[#655dc6] mb-2">لا توجد إقرارات</h3>
                <p className="text-slate-500">{consentSearch ? "لم يتم العثور على نتائج مطابقة" : "لم يتم إصدار أي إقرار بعد"}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredConsents.map(rec => (
                <ConsentCard
                  key={rec.id}
                  rec={rec}
                  onView={() => setSelectedConsent(rec)}
                  onDelete={() => handleDeleteConsent(rec.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* SERVICES CATALOG TAB */}
        <TabsContent value="services" className="space-y-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 mb-4">الخدمات المعروضة في نموذج الخطة العلاجية — أي إضافة أو حذف هنا يظهر فوراً دون الحاجة لتعديل الكود</p>
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[160px] space-y-1">
                  <label className="text-xs text-slate-500">اسم الخدمة بالعربية *</label>
                  <Input value={newSvcAr} onChange={e => setNewSvcAr(e.target.value)} placeholder="مثال: حشوات تجميلية" data-testid="new-svc-ar" />
                </div>
                <div className="flex-1 min-w-[160px] space-y-1">
                  <label className="text-xs text-slate-500">الاسم بالإنجليزية (اختياري)</label>
                  <Input value={newSvcEn} onChange={e => setNewSvcEn(e.target.value)} placeholder="COSMETIC RESTO" dir="ltr" data-testid="new-svc-en" />
                </div>
                <Button onClick={addService} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1.5" data-testid="add-svc-btn">
                  <Plus className="w-4 h-4" /> إضافة خدمة
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {svcCatalog.map(s => (
                  <div key={s.id} className="flex items-center justify-between bg-[#F5F3F9] rounded-lg px-3 py-2" data-testid={`svc-row-${s.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#655dc6] truncate">{s.ar}</p>
                      <p className="text-[10px] text-slate-400 truncate" dir="ltr">{s.en}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => deleteService(s)} className="text-red-400 hover:bg-red-50 h-7 w-7 p-0 shrink-0" data-testid={`del-svc-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              {svcCatalog.length === 0 && <p className="text-center text-sm text-slate-400 py-8">لا توجد خدمات</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* PLAN DETAILS DIALOG */}
      <Dialog open={!!selectedPlan} onOpenChange={(o) => !o && setSelectedPlan(null)}>
        <DialogContent className="sm:max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">تفاصيل الخطة العلاجية</DialogTitle>
          </DialogHeader>
          {selectedPlan && (
            <div className="space-y-3 text-sm">
              <div><span className="text-slate-500">اسم المراجع: </span><strong>{selectedPlan.patient_name}</strong></div>
              <div><span className="text-slate-500">رقم الملف: </span><strong>{selectedPlan.file_number || "—"}</strong></div>
              <div><span className="text-slate-500">رقم الهوية: </span><strong>{selectedPlan.national_id || "—"}</strong></div>
              <div><span className="text-slate-500">التاريخ: </span><strong>{formatDate(selectedPlan.created_at)}</strong></div>
              <div><span className="text-slate-500">الإجمالي: </span><strong className="text-[#655dc6]">{selectedPlan.total_price || "—"} ريال</strong></div>
              <div className="border-t pt-3">
                <div className="font-semibold mb-2 text-[#655dc6]">الخدمات:</div>
                <div className="space-y-2">
                  {(selectedPlan.services || []).map((s, i) => (
                    <div key={i} className="bg-slate-50 rounded p-2 flex justify-between items-center">
                      <span>{svcLabel(s.type)}</span>
                      <div className="flex gap-3 items-center">
                        <span className="text-slate-600 text-xs">
                          أسنان: {s.teeth?.join(", ") || "—"}
                        </span>
                        {s.price && (
                          <span className="text-[#655dc6] font-semibold text-xs">
                            {s.price} ريال
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {selectedPlan.notes && (
                <div className="border-t pt-3">
                  <div className="font-semibold mb-1 text-[#655dc6]">ملاحظات:</div>
                  <div className="bg-slate-50 rounded p-2 text-slate-700">{selectedPlan.notes}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CONSENT DETAILS DIALOG */}
      <Dialog open={!!selectedConsent} onOpenChange={(o) => !o && setSelectedConsent(null)}>
        <DialogContent className="sm:max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">تفاصيل الإقرار</DialogTitle>
          </DialogHeader>
          {selectedConsent && (
            <div className="space-y-3 text-sm">
              <div><span className="text-slate-500">نوع الإقرار: </span><strong className="text-[#655dc6]">{selectedConsent.consent_title}</strong></div>
              <div><span className="text-slate-500">اسم المريض: </span><strong>{selectedConsent.patient_name}</strong></div>
              <div><span className="text-slate-500">رقم الملف: </span><strong>{selectedConsent.file_number || "—"}</strong></div>
              <div><span className="text-slate-500">رقم الهوية: </span><strong>{selectedConsent.national_id || "—"}</strong></div>
              <div><span className="text-slate-500">التاريخ: </span><strong>{formatDate(selectedConsent.created_at)}</strong></div>
              {selectedConsent.teeth?.length > 0 && (
                <div><span className="text-slate-500">الأسنان: </span><strong>{selectedConsent.teeth.join(", ")}</strong></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TreatmentPlans;
